import { isInLineMiniApp, Product } from '@play-co/gcinstant';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { now } from 'src/StateObserver';
import { startInstallNativeSequence } from 'src/game/native/native.sequence';
import i18n from 'src/lib/i18n/i18n';
import { realMoneyPriceToReadableString } from 'src/lib/iap/iapConversion';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { captureGenericError } from 'src/lib/sentry';
import { ProductId, webIAPPrices } from 'src/replicant/iap/iap.ruleset';
import {
  match2BundleSaleGetPackRewardSet,
  match2BundleSaleHasCurrentEvent,
  match2BundleSaleIsFreeProduct,
} from 'src/replicant/match2/bundlesale/match2-bundlesale.getters';
import {
  BundleSaleProductId,
  bundleSaleProducts,
  bundleSaleRewards,
} from 'src/replicant/match2/bundlesale/match2-bundlesale.ruleset';
import { openMatch2RewardsPopup } from '../rewards/match2-rewards.utils';
import { Feature } from 'src/replicant/analytics';
import {
  getProductCatalog,
  isLineIAPAvailable,
} from 'src/game/iap/component/iap.api';
import { track } from 'src/lib/analytics/utils';
import { waitForIapResolve } from 'src/lib/iap/iap.util';

/**
 * Get the catalog of bundle sale products available for purchase, in gcinstant format
 */
export function match2BundleSaleGetWebIAPCatalog(): Product[] {
  // Get all paid products
  const products = bundleSaleProducts.filter(
    (id) => !bundleSaleRewards[id].free,
  ) as ProductId[];

  const catalog: Product[] = products.map((id) => {
    const priceJPY = webIAPPrices[id];
    return {
      id,
      title: id,
      description: id,
      imageURI: id,
      price: String(priceJPY),
      currencyCode: 'JPY',
    };
  });

  return catalog;
}

/**
 * Get the price string for a product id
 */
export function match2BundleSaleGetProductPriceStr(id: BundleSaleProductId) {
  // If the price is 0, it's a free product
  if (match2BundleSaleIsFreeProduct(id)) {
    return i18n('match2.bundleSale.free');
  }

  // LINE Mini App uses a different catalog
  if (isLineIAPAvailable()) {
    const lineProducts = getProductCatalog('line');

    // Find the catalog item corresponding to the product id
    const catalogItem = lineProducts.find((p) => p.id === id);

    // If no catalog item found, something has gone wrong
    if (!catalogItem) {
      // Report the error but don't throw it - let the UI handle the problem gracefully
      captureGenericError('ShopBundlesError', `No catalog item for ${id}`);
      return '';
    }

    return catalogItem.priceAsString;
  }

  // If platform catalog is empty, the shop must be in 'fake' mode,
  // so use the web catalog instead, like in fake gems shop
  const catalog = platform.catalog.length
    ? platform.catalog
    : match2BundleSaleGetWebIAPCatalog();

  // Find the catalog item corresponding to the product id
  const catalogItem = catalog.find((p) => p.id === id);

  // If no catalog item found, something has gone wrong
  if (!catalogItem) {
    // Report the error but don't throw it - let the UI handle the problem gracefully
    captureGenericError('ShopBundlesError', `No catalog item for ${id}`);
    return '';
  }

  return realMoneyPriceToReadableString(
    catalogItem.price,
    catalogItem.currencyCode,
  );
}

export async function match2BundleSaleCompletePurchase(
  productId: BundleSaleProductId | ProductId,
) {
  const bundleSaleProductId = productId as BundleSaleProductId;

  // Skip if product id is not a bundle sale pack
  if (!bundleSaleProducts.includes(bundleSaleProductId)) return;

  const rewards = match2BundleSaleGetPackRewardSet(bundleSaleProductId, now());

  // Paid gems packs have no extra rewards
  if (!rewards.length) return;
  await openMatch2RewardsPopup({
    rewards,
    useSfx: true,
    noGrantAnimation: true,
    origin: undefined,
    tracking: {
      feature: Feature.MATCH2.BUNDLE_SALE._,
      $subFeature: Feature.MATCH2.BUNDLE_SALE.REWARDS_POPUP,
    },
  });
}

export async function match2BundleSaleOpenPopup(
  origin: PopupDialogOrigin | undefined,
) {
  // Skip if no current event
  if (!match2BundleSaleHasCurrentEvent(now())) return;

  // Wait for IAP catalog to be ready before showing prices
  const ready = await waitForIapResolve('bundle_sale');
  if (!ready) {
    // Payments are not available, same path as a failed popup
    await startInstallNativeSequence({ originFeature: 'match2_bundle_sale' });
    return;
  }

  track('BundleSaleOpen', {
    isCatalogLoaded: platform.catalog.length > 0,
    origin,
  });

  // Open the bundle sale popup and collect the result
  const result = await openPopupPromise('popupMatch2BundleSale', { origin });

  // If closed or succeeded, no need to move any further
  if (result !== 'failed') return;

  // Run the install native sequence
  await startInstallNativeSequence({ originFeature: 'match2_bundle_sale' });
}
